import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Chip, CloseButton } from '@heroui/react';
import { Command } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { useRevenueDynamicsProductPicker } from './useRevenueDynamicsProductPicker';

type RevenueDynamicsProductPickerViewProps = ReturnType<
    typeof useRevenueDynamicsProductPicker
>;

/**
 * Render-only searchable product filter for the chart. Built on HeroUI Pro's
 * `Command` (palette pattern) so we get fuzzy search, keyboard navigation,
 * grouped sections, and an empty state for free.
 *
 * The trigger renders the current label inline; clearing the filter is handled
 * by the "All products" item AND by a separate clear-chip that surfaces next to
 * the trigger when a product is selected — so the active filter is visually
 * persistent (per the design-taste "active filter chip" rule). State + data
 * live in {@link useRevenueDynamicsProductPicker}.
 */
export const RevenueDynamicsProductPickerView: React.FC<
    RevenueDynamicsProductPickerViewProps
> = ({ isOpen, onOpenChange, selected, grouped, onSelect, onClear }) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            triggerLabel: t(
                'sales.widgets.revenueDynamics.productPicker.trigger',
            ),
            allLabel: t('sales.widgets.revenueDynamics.productPicker.all'),
            searchPlaceholder: t(
                'sales.widgets.revenueDynamics.productPicker.searchPlaceholder',
            ),
            empty: t('sales.widgets.revenueDynamics.productPicker.empty'),
            clearAria: t(
                'sales.widgets.revenueDynamics.productPicker.clearAria',
            ),
        }),
        [t],
    );

    return (
        <div
            className="flex items-center gap-2"
            {...getNodeTestIdProp('revenue-dynamics-product-picker')}
        >
            <Button
                className="min-w-0 max-w-xs justify-between gap-2"
                onPress={() => onOpenChange(true)}
                size="sm"
                variant="outline"
                {...getNodeTestIdProp('revenue-dynamics-product-trigger')}
            >
                <Icon
                    aria-hidden
                    className="size-3.5 shrink-0 text-muted"
                    icon="solar:box-bold-duotone"
                />
                <span
                    className="truncate text-xs"
                    {...getNodeTestIdProp(
                        'revenue-dynamics-product-trigger-label',
                    )}
                >
                    {selected ? selected.name : copy.allLabel}
                </span>
                <Icon
                    aria-hidden
                    className="size-3 shrink-0 text-muted"
                    icon="solar:alt-arrow-down-bold-duotone"
                />
            </Button>
            {selected ? (
                // HeroUI v3's `Chip` is presentational — it has no `onClose` and
                // renders no dismiss control of its own, so the clear action is
                // an explicit `CloseButton` inside it. The chip previously took
                // `onClose`, which type-checked against nothing and rendered
                // nothing: the filter could only be cleared from the "All
                // products" item in the palette.
                <Chip
                    color="success"
                    size="sm"
                    variant="soft"
                    {...getNodeTestIdProp('revenue-dynamics-product-badge')}
                >
                    <span className="max-w-32 truncate text-[11px]">
                        {selected.name}
                    </span>
                    {/* No children: they would *replace* the default close
                        icon rather than sit beside it, leaving a button with
                        nothing in it. The name goes on `aria-label` — safe
                        here because this trigger is not inside a Tooltip, which
                        is the wrapper that drops one. */}
                    <CloseButton
                        aria-label={copy.clearAria}
                        onPress={onClear}
                        {...getNodeTestIdProp('revenue-dynamics-product-clear')}
                    />
                </Chip>
            ) : null}

            <Command>
                <Command.Backdrop isOpen={isOpen} onOpenChange={onOpenChange}>
                    <Command.Container size="sm">
                        <Command.Dialog
                            {...getNodeTestIdProp(
                                'revenue-dynamics-product-dialog',
                            )}
                        >
                            <Command.InputGroup>
                                <Command.InputGroup.Prefix>
                                    <Icon
                                        aria-hidden
                                        className="size-3.5 text-muted"
                                        icon="solar:magnifer-bold-duotone"
                                    />
                                </Command.InputGroup.Prefix>
                                <Command.InputGroup.Input
                                    placeholder={copy.searchPlaceholder}
                                    {...getNodeTestIdProp(
                                        'revenue-dynamics-product-search',
                                    )}
                                />
                                <Command.InputGroup.ClearButton />
                            </Command.InputGroup>
                            <Command.List
                                renderEmptyState={() => (
                                    <div
                                        className="px-4 py-6 text-center text-sm text-muted"
                                        {...getNodeTestIdProp(
                                            'revenue-dynamics-product-empty',
                                        )}
                                    >
                                        {copy.empty}
                                    </div>
                                )}
                                {...getNodeTestIdProp(
                                    'revenue-dynamics-product-list',
                                )}
                            >
                                <Command.Item
                                    onAction={() => onSelect(null)}
                                    textValue={copy.allLabel}
                                    {...getNodeTestIdProp(
                                        'revenue-dynamics-product-option-all',
                                    )}
                                >
                                    <Icon
                                        aria-hidden
                                        className="size-3.5 text-muted"
                                        icon="solar:checklist-bold-duotone"
                                    />
                                    <span>{copy.allLabel}</span>
                                </Command.Item>
                                <Command.Separator />
                                {grouped.map(([category, items]) => (
                                    <Command.Group
                                        heading={category}
                                        key={category}
                                    >
                                        {items.map((product) => (
                                            <Command.Item
                                                key={product.id}
                                                onAction={() =>
                                                    onSelect(product.id)
                                                }
                                                textValue={product.name}
                                                {...getNodeTestIdProp(
                                                    `revenue-dynamics-product-option-${product.id}`,
                                                )}
                                            >
                                                <Icon
                                                    aria-hidden
                                                    className="size-3 text-muted"
                                                    icon="solar:box-bold-duotone"
                                                />
                                                <span className="truncate">
                                                    {product.name}
                                                </span>
                                            </Command.Item>
                                        ))}
                                    </Command.Group>
                                ))}
                            </Command.List>
                        </Command.Dialog>
                    </Command.Container>
                </Command.Backdrop>
            </Command>
        </div>
    );
};
